import {
  InvalidEntityData,
  InvalidEntityIssueType,
  InvalidEntityType,
  OrderItemDto,
} from "@deliverr/replenishment-client";

/**
 * Checks if a given order item has a corresponding invalid entity and requires inventory.
 * This is determined by matching the lineItemId and checking for a DUMMY_PACK_OF,
 * which indicates a product that could not be resolved.
 * @param orderItem - The order item to check.
 * @param invalidEntities - An array of invalid entities for the order.
 * @returns True if the order item is considered invalid and does not require inventory, false otherwise.
 */
export const doesOrderItemNotRequireInventory = (
  orderItem: OrderItemDto,
  invalidEntities: InvalidEntityData[] = []
): boolean => {
  return invalidEntities.some(
    (invalidEntity) =>
      invalidEntity.invalidEntityId &&
      invalidEntity.invalidEntityId === orderItem.lineItemId &&
      invalidEntity.invalidEntityType === InvalidEntityType.ORDER_ITEM &&
      getDskuFromInvalidEntity(invalidEntity) === undefined
  );
};

export const getDskuFromInvalidEntity = (invalidEntity: InvalidEntityData | undefined): string | undefined => {
  if (!invalidEntity) {
    return undefined;
  }

  switch (invalidEntity.issueType) {
    case InvalidEntityIssueType.CASE_PACK_NOT_FOUND:
      return invalidEntity.requestDetails?.[InvalidEntityIssueType.CASE_PACK_NOT_FOUND]?.dsku;
    case InvalidEntityIssueType.INSUFFICIENT_INVENTORY:
      return invalidEntity.requestDetails?.[InvalidEntityIssueType.INSUFFICIENT_INVENTORY]?.dsku;
    case InvalidEntityIssueType.LOT_NOT_FOUND:
      return invalidEntity.requestDetails?.[InvalidEntityIssueType.LOT_NOT_FOUND]?.dsku;
    default:
      return undefined;
  }
};

export const invalidProductRelatedIssues = [
  InvalidEntityIssueType.PRODUCT_NOT_FOUND,
  InvalidEntityIssueType.INSUFFICIENT_INVENTORY,
  InvalidEntityIssueType.CASE_PACK_NOT_FOUND,
  InvalidEntityIssueType.MULTIPLE_MATCHING_DSKUS,
  InvalidEntityIssueType.LOT_NOT_FOUND,
];
